// MACHINERY V2 (Stroje) — SR detail: the urgency + resolution chips and the two
// time formatters (M2.6).
//
// Spec: data package §4 / §8 (the read model). Reference impls:
// design_handoff_machinery_v3/machinery-sr.jsx `SrUrgencyChip` (lines 128-145)
// and `SrResolutionChip` (:108-121); artboards 0700 / 0701.
//
// THE STATUS AND SOURCE CHIPS ARE NOT HERE ON PURPOSE. `SrStatusChip` and
// `SrSourceChip` already exist in profile/tabs/srChips.tsx (M2.5) and the detail
// page IMPORTS them — including FIX 1 (sourceType 4 = MIDAS). A second copy
// would be the exact drift the ticket keeps warning about.
//
// URGENCY IS 1 / 2 / 4. There is no 3 and no "medium" key (§7, §13). The colours
// are v1's, from srDetail/SrHeaderStrip.tsx:24-28 — mapped onto the palette
// tokens where the palettes agree, kept literal where they do not (urgent red
// and high amber differ per palette only in the token, so `--red` / `--amber`
// carry them; low green is `--green`).
//
// TIME FORMATTING IS NOT LOCALE-DERIVED, deliberately — the same rule
// profile/tabs/tabFormat.ts states: switching language must not reorder a
// maintenance timestamp. `formatTabDate` is imported for exactly that reason
// rather than reimplemented; only the DURATION helper lives here, because it is
// SR-specific (v1: srDetail/ServiceReportDetailView.tsx:46-59).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

const URGENCY: Record<number, { token: string; labelKey: string }> = {
  1: { token: "--red", labelKey: "myMachinery:priorities.urgent" },
  2: { token: "--amber", labelKey: "myMachinery:priorities.high" },
  4: { token: "--green", labelKey: "myMachinery:priorities.low" },
};

const chipBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 22,
  padding: "0 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

/** `d h` between two instants — v1's `computeDuration`, verbatim. */
export const computeDuration = (from: Date | string, to: Date | string): string => {
  const diffMs = Math.abs(new Date(to).getTime() - new Date(from).getTime());
  const totalHours = Math.floor(diffMs / 3600000);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days === 0 && hours === 0) return "< 1h";
  if (days === 0) return `${hours}h`;
  if (hours === 0) return `${days}d`;
  return `${days}d ${hours}h`;
};

export const SrUrgencyChip: React.FC<{ urgency?: number }> = ({ urgency }) => {
  const { translate } = useTranslation("myMachinery");
  if (urgency === undefined || urgency === null) return null;
  const cfg = URGENCY[urgency];
  // An unmapped value renders nothing, exactly as v1's
  // `urgencyConfig[serviceReport.urgency] &&` guard does.
  if (!cfg) return null;

  return (
    <span
      style={{
        ...chipBase,
        color: `color-mix(in oklab, var(${cfg.token}) 80%, var(--ink))`,
        background: `color-mix(in oklab, var(${cfg.token}) 12%, var(--card))`,
        border: `1px solid color-mix(in oklab, var(${cfg.token}) 36%, var(--border))`,
      }}
    >
      <span
        aria-hidden
        style={{ width: 5, height: 5, borderRadius: 999, background: `var(${cfg.token})` }}
      />
      {translate(cfg.labelKey)}
    </span>
  );
};

/** "Vyriešené za 14h" when solved, "Otvorené 3d 2h" while in progress — v1's
 *  `resolutionText` (ServiceReportDetailView.tsx:163-175) as a chip. */
export const SrResolutionChip: React.FC<{
  openDate?: Date;
  closeDate?: Date;
  isSolved: boolean;
}> = ({ openDate, closeDate, isSolved }) => {
  const { translate } = useTranslation("myMachinery");
  if (!openDate) return null;

  const solved = isSolved && !!closeDate;
  const duration = computeDuration(openDate, solved ? (closeDate as Date) : new Date());
  const token = solved ? "--green" : "--accent";
  const label = solved
    ? translate("myMachinery:srDetail.resolvedIn", { duration })
    : translate("myMachinery:srDetail.openFor", { duration });

  return (
    <span
      style={{
        ...chipBase,
        color: `color-mix(in oklab, var(${token}) 80%, var(--ink))`,
        background: `color-mix(in oklab, var(${token}) 12%, var(--card))`,
        border: `1px solid color-mix(in oklab, var(${token}) 36%, var(--border))`,
      }}
    >
      <svg
        viewBox="0 0 10 10"
        width="10"
        height="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        aria-hidden
      >
        <circle cx="5" cy="5" r="3.8" />
        <path d="M5 2.6 V5 L6.7 6" />
      </svg>
      {label}
    </span>
  );
};
